import { ErrorMessage } from '@folio/components/module-elements/ErrorMessage';
import {
  DocumentProcessingStatus,
  DocumentViewer,
  type DocumentViewerDocument,
  isDocumentClassificationProcessing,
  useDocumentViewer,
  useRenderDocumentAiBadges,
} from '@folio/components/features/document-viewer';
import { useAssertedParams } from '@folio/lib';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  getKorintErrorStatus,
  keyValueFieldsToObject,
  policyControllerListPolicyDocumentsQueryOptions,
  policyDocumentControllerGetPolicyDocumentConformityQueryOptions,
  policyDocumentControllerGetPolicyDocumentFraudQueryOptions,
  policyDocumentExtractionControllerGetPolicyExtractionsQueryOptions,
  documentValidityControllerGetPolicyValidityQueryOptions,
  documentCoherenceControllerGetDocumentCoherenceQueryOptions,
  useDocumentValidityControllerOverrideDocumentValidity,
  useDocumentExtractionControllerSubmitExtractionFeedback,
  usePolicyDocumentControllerAccept,
  usePolicyDocumentControllerAcceptAll,
  usePolicyDocumentControllerCancel,
  usePolicyDocumentControllerCorrectPolicyDocumentFraudVerdict,
  usePolicyDocumentControllerReject,
  uploadDocumentsOnPolicy,
  usePolicyControllerDeletePolicyDocuments,
  type CorrectPolicyDocumentFraudVerdictInputBody,
  type DocumentValidityRuleDto,
  type GetDocumentExtractionOutput,
  type SubmitFeedbackBody,
} from 'korint-sdk';
import { useDocumentConformityMap } from '@folio/hooks/legacy/useDocumentConformityMap';
import {
  useDocumentsAutoClassification,
  useKorintConfig,
  getStoredSession,
  useFeatureFlag,
  type AIValidityResult,
} from 'korint-sdk/react';
import { useCallback, useMemo, useState } from 'react';
import { AlertCircleIcon, FolderIcon, RotateCcwIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components';
import { DocumentsCollection } from '../../../components/module-elements/Documents/legacy/DocumentsCollection';
import { getUploadedDocumentGroups } from '../../../components/module-elements/Documents/uploaded-document-groups';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  ConformityAcceptAllControl,
  ConformityReadOnlyBadge,
  ConformityReuploadZone,
  type DocumentConformityState,
} from '../../DocumentsListsPolicyModule/legacy/DocumentsListsPolicyModule';
import { getCategoryConformitySummary } from '../../DocumentsListsPolicyModule/conformity-summary';
import type { DocumentCategoryType } from '@folio/utils/useDocumentUploadMode';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useTranslation } from 'react-i18next';
import { isUnresolvedCategory } from '@folio/utils/document-categories';
import { getMinSignaturePages } from '@folio/utils/getMinSignaturePages';
import {
  getDocumentReview,
  type DocumentReviewFilter,
  type DocumentReviewSummary,
} from './document-review';
import { useDocumentUploadQueue } from '../useDocumentUploadQueue';
import { FRAUD_VERDICT_POLL_INTERVAL_MS, isAwaitingFraudVerdict } from '../fraud-verdict-polling';
import { DocumentUploadQueue } from '../DocumentUploadQueue';
import { DocumentReviewFilters } from './DocumentReviewFilters';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type DisplayContext = 'policyholder' | 'broker';
export type DocumentsUploadPolicyModuleProps = {
  /**
   * Defines the context in which the module is displayed: 'policyholder' or 'broker'.
   * In the 'broker' context, it allows to delete documents.
   */
  displayContext: DisplayContext;

  /**
   * Delete gate for callers whose right to delete follows a lifecycle, not the audience (the stop
   * funnel). Defaults to the broker context; `readOnly` still wins.
   */
  canDeleteDocuments?: boolean;

  /**
   * Extra actions to display in the module. This is an escape hatch to display components
   * that are not part of the module.
   */
  extraActions?: React.ReactNode;

  /**
   * The function to be called when a document is uploaded.
   */
  onUpload?: () => void;

  /**
   * Whether to use the branch ID.
   */
  useBranchId?: boolean;

  /**
   * Branch to upload to, for callers whose route carries no :branchId (the stop funnel reads it
   * from the draft). Wins over the route param.
   */
  branchId?: string;

  /**
   * The authorized formats for the documents.
   */
  authorizedFormats?: Record<string, string[]>;

  /**
   * The documents lists to display in the module.
   */
  documentsLists: {
    category: string;
    documentType?: string;
    required?: boolean;
    validity?: DocumentValidityRuleDto[];
  }[];

  /**
   * Category type for determining upload mode. If not specified, falls back to global uploadMode.
   * @default undefined (uses global uploadMode)
   */
  categoryType?: DocumentCategoryType;

  /** Review-only: no dropzone and no delete, for a validator acting on what was provided. */
  readOnly?: boolean;

  /** Hides the summary/accept-all button for callers with an equivalent control elsewhere.
   * @default false
   */
  hideAcceptAllButton?: boolean;

  /** Uses the review filters, concise category labels, and tree hierarchy. */
  reviewLayout?: boolean;

  conformityByDocumentId?: Map<string, DocumentConformityState>;
  onReuploadDocumentConformity?: (documentId: string, file: File) => void;
};

function NoResultsRow() {
  const { t } = useTranslation();
  return <ErrorMessage>{t('documentsUploadPolicy.noResults')}</ErrorMessage>;
}

type TimedOutDoc = Pick<DocumentViewerDocument, 'type' | 'category'> & {
  id: string;
  name: string;
  signedUrl?: string;
  createdAt: string;
  metadata: Record<string, unknown>;
};

function TimedOutDocsSection({
  docs,
  categories,
  onRetryAll,
  isRetrying,
  onView,
  onDelete,
  isBroker,
}: {
  docs: TimedOutDoc[];
  categories: { category: string; required?: boolean }[];
  onRetryAll: () => void;
  isRetrying: boolean;
  onView?: (doc: TimedOutDoc) => void;
  onDelete?: (doc: TimedOutDoc) => Promise<unknown>;
  isBroker: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="border border-amber-200 rounded-lg overflow-hidden mb-4">
      <Group justify="between" gap={2} className="w-full px-3 py-3 md:py-2.5 bg-amber-50">
        <Group gap={2} className="flex-1 min-w-0">
          <FolderIcon className="w-4 h-4 flex-shrink-0 text-amber-700" />
          <Text inline size="sm" weight="medium" tone="default">
            {t('aiClassification.timedOutFolderTitle')}
          </Text>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 text-white flex-shrink-0">
            <AlertCircleIcon className="w-3 h-3" />
            <Text inline size="xs" weight="medium">
              {t('aiClassification.timedOutCount', { count: docs.length })}
            </Text>
          </span>
        </Group>
        <Group className="flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isRetrying}
            onClick={onRetryAll}
          >
            <RotateCcwIcon className="h-3.5 w-3.5 mr-1.5" />
            {t('aiClassification.retryAll')}
          </Button>
        </Group>
      </Group>
      <DocumentsCollection
        groups={[
          { id: 'unclassified', label: t('fileInput.uncategorizedFolderTitle'), documents: docs },
        ]}
        onView={onView}
        onDelete={onDelete}
        classificationCategories={isBroker ? categories : undefined}
        showGroupHeaders={false}
      />
    </div>
  );
}

export function DocumentsUploadPolicyModule({
  displayContext,
  canDeleteDocuments,
  extraActions,
  onUpload,
  useBranchId,
  branchId: branchIdProp,
  authorizedFormats,
  documentsLists,
  categoryType = 'policyholderDocuments',
  readOnly = false,
  hideAcceptAllButton = false,
  reviewLayout = false,
  conformityByDocumentId: providedConformityByDocumentId,
  onReuploadDocumentConformity,
}: DocumentsUploadPolicyModuleProps) {
  const { t } = useTranslation();
  const isNonDelegatedDocuments = categoryType === 'nonDelegatedDocuments';
  const title = t([
    `documentsUploadPolicy.${displayContext}.title`,
    `documentsUploadPolicy.${categoryType}.title`,
    'documentsUploadPolicy.title',
  ]);
  const subtitle = t(
    [
      `documentsUploadPolicy.${displayContext}.${categoryType}.subtitle`,
      `documentsUploadPolicy.${displayContext}.subtitle`,
      'documentsUploadPolicy.subtitle',
    ],
    '',
  );
  const hasReviewLayout = displayContext === 'broker' || reviewLayout;
  const cardTitle = hasReviewLayout && !isNonDelegatedDocuments ? t('documentReview.title') : title;
  const { policyId, branchId: branchIdParam } = useAssertedParams<{
    policyId: string;
    branchId?: string;
  }>();

  // Always use the batch/funnel-style rendering on the dashboard.
  const uploadMode = 'batch' as const;

  const sortedDocumentsLists = useMemo(
    () => [...documentsLists].sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0)),
    [documentsLists],
  );

  const uploadType = isNonDelegatedDocuments ? 'non_delegated' : 'supporting_document';
  const classificationConfigKey =
    categoryType === 'policyholderDocuments' ? 'policyholderDashboard' : categoryType;
  const branchId = branchIdProp ?? (useBranchId ? branchIdParam : undefined);

  const { clientOptions, korintConfig } = useKorintConfig();
  const aiClassificationDisplayConfig =
    korintConfig.modulesConfig?.aiClassification?.interfaces?.[classificationConfigKey];
  // Only used by single-tenant apps, where tenant is always set.
  const getAccessToken = useCallback(
    () => getStoredSession(clientOptions.tenant ?? '')?.accessToken,
    [clientOptions.tenant],
  );

  const queryOptions = policyControllerListPolicyDocumentsQueryOptions(policyId, { branchId });

  const {
    data: allDocuments,
    isLoading: isLoadingDocuments,
    isError: documentsError,
  } = useQuery({
    ...queryOptions,
    select: data =>
      data.data.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
  });

  const managedDocumentTypes = useMemo(
    () =>
      new Set(
        documentsLists
          .map(doc => doc.documentType)
          .filter(Boolean)
          .map(String),
      ),
    [documentsLists],
  );

  const viewableDocuments = useMemo(() => {
    if (!allDocuments) return [];

    const filtered = allDocuments.filter(document => {
      if (document.type !== uploadType) return false;
      if (
        uploadType === 'non_delegated' &&
        (document.metadata.signed === 'true' || document.metadata.archived === 'true')
      )
        return false;
      return (
        isUnresolvedCategory(document.category) ||
        documentsLists.some(
          configuredDocument => configuredDocument.category === document.category,
        ) ||
        managedDocumentTypes.has(document.category ?? '')
      );
    });

    const groups = new Map<string, typeof filtered>();
    filtered.forEach(doc => {
      const rawCategory = doc.category ? String(doc.category) : 'uncategorized';
      const category = isUnresolvedCategory(rawCategory) ? 'uncategorized' : rawCategory;
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)?.push(doc);
    });

    const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'uncategorized') return -1;
      if (b === 'uncategorized') return 1;
      return a.localeCompare(b);
    });

    const sorted = sortedGroups.flatMap(([, docs]) => docs);

    return sorted.map(doc => ({
      id: doc.id,
      name: doc.name,
      signedUrl: doc.signedUrl || '',
      createdAt: doc.createdAt,
      type: doc.type,
      category: doc.category,
      metadata: Object.fromEntries(
        Object.entries(doc.metadata).map(([key, value]) => [
          key,
          value === null || value === undefined
            ? undefined
            : typeof value === 'object'
              ? (value as Record<string, unknown>)
              : String(value),
        ]),
      ) as Record<string, string | number | boolean | Record<string, unknown> | undefined>,
    }));
  }, [allDocuments, documentsLists, uploadType, managedDocumentTypes]);

  const {
    selectedDocument,
    selectDocument: selectViewerDocument,
    closeViewer,
  } = useDocumentViewer(viewableDocuments);
  const [viewerDocumentIds, setViewerDocumentIds] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const invalidateOnClassificationChange = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: policyControllerListPolicyDocumentsQueryOptions(policyId, { branchId }).queryKey,
    });
  }, [queryClient, policyId, branchId]);

  const refreshDocumentAnalysis = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: policyControllerListPolicyDocumentsQueryOptions(policyId, { branchId }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: policyDocumentExtractionControllerGetPolicyExtractionsQueryOptions(policyId, {
        branchId,
      }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: documentValidityControllerGetPolicyValidityQueryOptions(policyId, {
        branchId,
      }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: documentCoherenceControllerGetDocumentCoherenceQueryOptions(policyId, {
        branchId,
      }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: policyDocumentControllerGetPolicyDocumentFraudQueryOptions(policyId, {
        branchId: branchId ?? 'BASE',
      }).queryKey,
    });
  }, [queryClient, policyId, branchId]);

  const {
    processDocuments: processClassificationDocuments,
    isProcessing: isClassifying,
    classificationStates,
  } = useDocumentsAutoClassification({
    getAccessToken,
    onDocumentValidated: invalidateOnClassificationChange,
    // A broker's stream completes only after the fraud check, so refresh once validity lands.
    onDocumentValidityChecked: refreshDocumentAnalysis,
    onAllComplete: refreshDocumentAnalysis,
  });

  // A single configured document uses its preset category before AI classification runs.
  const presetDocumentType =
    documentsLists.length === 1 ? documentsLists[0].documentType : undefined;

  const presetCategory = documentsLists.length === 1 ? documentsLists[0].category : undefined;

  // When a single document type is expected, the type is stamped directly on
  // upload (no AI classification), so dropping several files all land on that
  // one type. Block multi-file drops to prevent duplicate-type documents that
  // would later break signature generation (NO_UNIQUE_DOCUMENT_TYPE).
  const allowMultiple = documentsLists.length !== 1;

  const uploadQueue = useDocumentUploadQueue({
    upload: file =>
      uploadDocumentsOnPolicy({
        policyId,
        branchId,
        payload: {
          file,
          type: uploadType,
          category: presetDocumentType ?? presetCategory ?? 'uncategorized',
          metadata: {},
          name: file.name,
        },
      }),
    onUploaded: documentIds => {
      void processClassificationDocuments(documentIds);
      onUpload?.();
    },
  });

  // Source of truth for "this doc needs manual classification" is the persisted doc
  // metadata, not the in-memory hook state — this way the section survives a refresh
  // even after the SSE connection that timed out is gone.
  const unclassifiedDocs = useMemo<TimedOutDoc[]>(() => {
    // No AI classification for this tenant/product → uploaded docs are never auto-classified,
    // so there is nothing to "retry". Skip the timed-out recovery section (and its
    // "Classify with AI" button) entirely; these docs flow into displayDocuments normally.
    if (displayContext !== 'broker' || !aiClassificationDisplayConfig?.enabled) return [];
    if (!viewableDocuments.length) return [];
    return viewableDocuments
      .filter(d => {
        const scanResult = d.metadata.scanresult ? String(d.metadata.scanresult) : '';
        if (scanResult !== 'CLEAN' && scanResult !== 'UNSUPPORTED') return false;
        const cat = d.category ? String(d.category) : '';
        if (!isUnresolvedCategory(cat)) return false;
        const state = classificationStates.get(d.id);
        if (isDocumentClassificationProcessing(state)) return false;
        if (state) {
          // SSE just landed on a real category — hide until the documents query
          // refetch reflects it, otherwise the yellow "À classer manuellement"
          // panel flashes for the window between SSE completion and React
          // Query's invalidation cascade.
          if (
            state.status === 'completed' &&
            state.result?.category &&
            !isUnresolvedCategory(state.result.category)
          ) {
            return false;
          }
        }
        return true;
      })
      .map(
        (d): TimedOutDoc => ({
          id: d.id,
          name: d.name,
          signedUrl: d.signedUrl ?? undefined,
          createdAt: d.createdAt,
          type: d.type,
          category: d.category,
          metadata: d.metadata,
        }),
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [viewableDocuments, classificationStates, aiClassificationDisplayConfig, displayContext]);

  const unclassifiedDocumentIds = useMemo(
    () => unclassifiedDocs.map(d => d.id),
    [unclassifiedDocs],
  );

  // Documents shown in the funnel-style list — user uploads that are already classified
  // (or being classified). Timed-out unclassified docs render in their own dedicated
  // section above (TimedOutDocsSection) to keep the recovery action separate.
  const displayDocuments = useMemo(() => {
    const unclassifiedSet = new Set(unclassifiedDocumentIds);
    return viewableDocuments.filter(d => !unclassifiedSet.has(d.id));
  }, [viewableDocuments, unclassifiedDocumentIds]);

  const conformityCategoryByDocumentId = useMemo(
    () =>
      new Map(
        getUploadedDocumentGroups({
          documents: displayDocuments,
          categories: sortedDocumentsLists,
          translate: t,
        }).flatMap(({ id: category, documents }) =>
          category === 'uncategorized'
            ? []
            : documents.map(({ id: documentId }) => [documentId, category] as const),
        ),
      ),
    [displayDocuments, sortedDocumentsLists, t],
  );

  const handleRetryTimedOutClassifications = useCallback(() => {
    if (unclassifiedDocumentIds.length > 0) {
      processClassificationDocuments(unclassifiedDocumentIds, { force: true });
    }
  }, [processClassificationDocuments, unclassifiedDocumentIds]);

  const { policy } = usePolicyOnDashboard();

  const { data: publicConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });

  const extractionEnabled = useFeatureFlag('perm_ai_extraction', policy.productId);
  const validityEnabled = useFeatureFlag('perm_ai_validity', policy.productId);
  const coherenceEnabled =
    useFeatureFlag('ops_ai_coherence', policy.productId) && displayContext === 'broker';

  // For a single expected document type (non-delegated upload), require the
  // dropped PDF to have at least as many pages as the highest fixed signature
  // anchor page, so the signature can't land on a missing page. Derived from
  // the signature config — `page: 'LATEST'`/tag-based docs impose no minimum.
  const minPages = getMinSignaturePages(publicConfig?.signature, presetDocumentType);

  const { data: extractions } = useQuery({
    ...policyDocumentExtractionControllerGetPolicyExtractionsQueryOptions(policyId, { branchId }),
    enabled: displayContext === 'broker' && extractionEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.extractions) return false;
      const hasPending = data.extractions.some(e => e.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const {
    data: validities,
    isLoading: isLoadingValidity,
    isError: validityError,
  } = useQuery({
    ...documentValidityControllerGetPolicyValidityQueryOptions(policyId, {
      branchId,
    }),
    enabled: displayContext === 'broker' && validityEnabled && (!useBranchId || !!branchId),
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.validities) return false;
      const hasPending = data.validities.some(c => c.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const { data: coherence } = useQuery({
    ...documentCoherenceControllerGetDocumentCoherenceQueryOptions(policyId, { branchId }),
    enabled: coherenceEnabled && (!useBranchId || !!branchId),
    refetchInterval: query =>
      query.state.data?.documents.some(({ status }) => status === 'PENDING') ? 2000 : false,
  });

  const getExtractionForDocument = useCallback(
    (documentId: string): GetDocumentExtractionOutput | undefined => {
      return extractions?.extractions?.find(e => e.documentId === documentId);
    },
    [extractions],
  );

  const validityByDocumentId = useMemo(
    () =>
      new Map(
        (validities?.validities ?? [])
          .filter(c => c.status !== 'PENDING')
          .map(c => [c.documentId, c as AIValidityResult]),
      ),
    [validities],
  );

  const selectedDocumentExtraction = selectedDocument
    ? getExtractionForDocument(selectedDocument.id)
    : undefined;

  const selectedDocumentValidity = selectedDocument
    ? validityByDocumentId.get(selectedDocument.id)
    : undefined;

  const coherenceByDocumentId = useMemo(
    () =>
      new Map(
        (coherenceEnabled ? (coherence?.documents ?? []) : []).map(document => [
          document.documentId,
          document,
        ]),
      ),
    [coherence, coherenceEnabled],
  );

  const { mutateAsync: submitFeedback, isPending: isFeedbackPending } =
    useDocumentExtractionControllerSubmitExtractionFeedback({
      mutation: {
        onSuccess: () => {
          toast({ description: t('documents.extraction.feedback.submitted') });
        },
        onError: () => {
          toast({
            description: t('documents.extraction.feedback.error'),
            variant: 'destructive',
          });
        },
      },
    });

  const handleExtractionFeedback = useCallback(
    (feedback: SubmitFeedbackBody) => {
      if (!selectedDocument) return;
      return submitFeedback({ documentId: selectedDocument.id, data: feedback }).then(
        () => undefined,
      );
    },
    [selectedDocument, submitFeedback],
  );

  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: [
      'OVERRIDE_DOCUMENT_VALIDITY',
      'REVIEW_POLICY_DOCUMENT',
      'GET_POLICY_DOCUMENT_FRAUD',
      'REVIEW_POLICY_DOCUMENT_FRAUD',
    ],
  });
  const canOverrideValidity = can.OVERRIDE_DOCUMENT_VALIDITY;
  const canDecideConformity = displayContext === 'broker' && can.REVIEW_POLICY_DOCUMENT;
  const canViewFraud = displayContext === 'broker' && can.GET_POLICY_DOCUMENT_FRAUD;
  const canReviewFraud = canViewFraud && can.REVIEW_POLICY_DOCUMENT_FRAUD && !readOnly;

  const { data: fraudResults, refetch: refetchFraud } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentFraudQueryOptions(policyId, {
      branchId: branchId ?? 'BASE',
    }),
    enabled: canViewFraud && (!useBranchId || !!branchId),
    // A refresh or dropped stream loses the live verdict; scoring keeps running server-side.
    refetchInterval: query =>
      isAwaitingFraudVerdict({
        documents: viewableDocuments,
        scores: query.state.data?.scores ?? [],
        now: Date.now(),
      })
        ? FRAUD_VERDICT_POLL_INTERVAL_MS
        : false,
  });

  const { mutate: correctFraudVerdict, isPending: isCorrectingFraudVerdict } =
    usePolicyDocumentControllerCorrectPolicyDocumentFraudVerdict({
      mutation: {
        onSuccess: () => refetchFraud(),
        onError: error => {
          if (getKorintErrorStatus(error) === 409) void refetchFraud();
        },
      },
    });
  const handleCorrectFraudVerdict = useCallback(
    (documentId: string, correction: CorrectPolicyDocumentFraudVerdictInputBody) =>
      correctFraudVerdict({ policyId, documentId, data: correction }),
    [correctFraudVerdict, policyId],
  );

  const selectedDocumentStream = selectedDocument
    ? classificationStates.get(selectedDocument.id)
    : undefined;
  const selectedDocumentFraud = selectedDocument
    ? (fraudResults?.scores.find(({ documentId }) => documentId === selectedDocument.id) ??
      selectedDocumentStream?.fraud)
    : undefined;

  const { mutate: overrideValidity, isPending: isValidityOverriding } =
    useDocumentValidityControllerOverrideDocumentValidity({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: documentValidityControllerGetPolicyValidityQueryOptions(policyId, {
              branchId,
            }).queryKey,
          });
          toast({ description: t('documents.validity.overrideSuccess') });
        },
        onError: () => {
          toast({
            description: t('documents.validity.overrideError'),
            variant: 'destructive',
          });
        },
      },
    });

  const handleValidityOverride = useCallback(
    (documentId: string, newStatus: 'VALID' | 'INVALID') => {
      overrideValidity({ policyId, documentId, data: { status: newStatus } });
    },
    [overrideValidity, policyId],
  );

  const { mutateAsync: deleteDocument } = usePolicyControllerDeletePolicyDocuments({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      },
      onError: () => {
        toast({
          description: t('documentsUploadPolicy.deleteError'),
          variant: 'destructive',
        });
      },
    },
  });

  // One condition for every delete surface: a reviewer must not be able to remove the proof they
  // are judging, whichever one they reach it through.
  const canDelete = (canDeleteDocuments ?? displayContext === 'broker') && !readOnly;

  const handleDeleteDocument = canDelete
    ? async (document: DocumentViewerDocument) => {
        await deleteDocument({
          policyId,
          params: { ids: [document.id], branchId },
        });
      }
    : undefined;

  // Base branch's real id is the literal string "BASE" (see PolicyAggregateProjection).
  const conformityBranchId = branchId ?? 'BASE';

  const documentConformityFlagEnabled = useFeatureFlag(
    'feat_document_conformity',
    policy.productId,
  );

  const canReviewConformity = displayContext === 'broker' && documentConformityFlagEnabled;
  const {
    data: conformityData,
    isSuccess: isConformityLoaded,
    refetch: refetchConformity,
  } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policyId, {
      branchId: conformityBranchId,
    }),
    retry: false,
    enabled: canReviewConformity,
  });
  // A disabled query still serves another caller's cached data with isSuccess: true.
  const isDocumentReviewEnabled = canReviewConformity && isConformityLoaded;

  const { conformityByDocumentId, conformityIdByDocumentId } = useDocumentConformityMap(
    allDocuments,
    conformityData?.conformities,
    isDocumentReviewEnabled,
  );
  const categoryConformitySummary = useMemo(
    () =>
      categoryType === 'policyholderDocuments'
        ? getCategoryConformitySummary({
            categoryByDocumentId: conformityCategoryByDocumentId,
            conformityByDocumentId,
          })
        : undefined,
    [categoryType, conformityByDocumentId, conformityCategoryByDocumentId],
  );
  const visibleConformityByDocumentId = providedConformityByDocumentId ?? conformityByDocumentId;

  const { mutate: acceptDocumentConformityMutation } = usePolicyDocumentControllerAccept({
    mutation: { onSuccess: () => refetchConformity() },
  });
  const { mutate: rejectDocumentConformityMutation } = usePolicyDocumentControllerReject({
    mutation: { onSuccess: () => refetchConformity() },
  });
  const { mutate: cancelDocumentConformityMutation } = usePolicyDocumentControllerCancel({
    mutation: { onSuccess: () => refetchConformity() },
  });
  const { mutate: acceptAllDocumentConformityMutation } = usePolicyDocumentControllerAcceptAll({
    mutation: {
      onSuccess: () => refetchConformity(),
      onError: () => {
        toast({
          title: t('documentsListsPolicy.conformity.acceptAllErrorTitle'),
          description: t('documentsListsPolicy.conformity.acceptAllError'),
          variant: 'destructive',
        });
      },
    },
  });

  const onAcceptDocumentConformity = useCallback(
    (documentId: string) =>
      acceptDocumentConformityMutation({
        policyId,
        documentId,
        data: { branchId: conformityBranchId },
      }),
    [acceptDocumentConformityMutation, policyId, conformityBranchId],
  );
  const onRejectDocumentConformity = useCallback(
    (documentId: string, reason: string) =>
      rejectDocumentConformityMutation({
        policyId,
        documentId,
        data: { branchId: conformityBranchId, reason },
      }),
    [rejectDocumentConformityMutation, policyId, conformityBranchId],
  );
  const onCancelDocumentConformity = useCallback(
    (documentId: string) => {
      const conformityId = conformityIdByDocumentId.get(documentId);
      if (conformityId) cancelDocumentConformityMutation({ policyId, conformityId });
    },
    [cancelDocumentConformityMutation, conformityIdByDocumentId, policyId],
  );
  const onAcceptAllDocumentConformity = useCallback(
    () => acceptAllDocumentConformityMutation({ policyId, data: { branchId: conformityBranchId } }),
    [acceptAllDocumentConformityMutation, policyId, conformityBranchId],
  );

  const [reviewFilter, setReviewFilter] = useState<DocumentReviewFilter>('all');
  const reviewByDocumentId = new Map(
    viewableDocuments.map(document => [
      document.id,
      getDocumentReview({
        document,
        classification: classificationStates.get(document.id),
        extraction: getExtractionForDocument(document.id),
        validity: validities?.validities?.find(({ documentId }) => documentId === document.id),
        validityEnabled,
        coherence: coherenceByDocumentId.get(document.id),
        requiresConformityDecision:
          canDecideConformity &&
          visibleConformityByDocumentId.get(document.id)?.decision === 'PENDING',
      }),
    ]),
  );
  const requiredCategories = sortedDocumentsLists.filter(({ required }) => required);
  const missingRequiredCategories = requiredCategories.filter(
    ({ category, documentType }) =>
      !viewableDocuments.some(
        document =>
          document.category === category || (documentType && document.category === documentType),
      ),
  );
  const filteredCategories =
    reviewFilter === 'missing' ? missingRequiredCategories : sortedDocumentsLists;
  const reviewSummary: DocumentReviewSummary = {
    receivedCount: viewableDocuments.length,
    missingRequiredCount: missingRequiredCategories.length,
    attentionCount: [...reviewByDocumentId.values()].filter(({ status }) => status === 'attention')
      .length,
    processingCount:
      [...reviewByDocumentId.values()].filter(({ status }) => status === 'processing').length +
      uploadQueue.attempts.filter(
        attempt =>
          attempt.status === 'uploading' ||
          (attempt.status === 'uploaded' &&
            !viewableDocuments.some(({ id }) => id === attempt.documentId)),
      ).length,
    isLoading: isLoadingDocuments || (validityEnabled && isLoadingValidity),
    hasError: documentsError || (validityEnabled && validityError),
  };
  const filteredDocuments =
    reviewFilter === 'missing'
      ? []
      : reviewFilter === 'attention'
        ? displayDocuments.filter(({ id }) => reviewByDocumentId.get(id)?.status === 'attention')
        : displayDocuments;
  const showUnclassifiedDocuments =
    !hasReviewLayout || reviewFilter === 'all' || reviewFilter === 'attention';
  const emptyCategories = isNonDelegatedDocuments
    ? 'all'
    : hasReviewLayout
      ? reviewFilter === 'attention'
        ? 'hidden'
        : 'required'
      : 'all';

  const viewerDocuments = viewerDocumentIds
    .map(documentId => viewableDocuments.find(({ id }) => id === documentId))
    .filter((document): document is DocumentViewerDocument => document !== undefined);
  const openDocumentViewer = useCallback(
    (document: DocumentViewerDocument) => {
      const documentsForViewer =
        reviewFilter === 'attention'
          ? viewableDocuments.filter(({ id }) => reviewByDocumentId.get(id)?.status === 'attention')
          : reviewFilter === 'missing'
            ? []
            : viewableDocuments;
      setViewerDocumentIds(documentsForViewer.map(({ id }) => id));
      selectViewerDocument(document);
    },
    [reviewByDocumentId, reviewFilter, selectViewerDocument, viewableDocuments],
  );
  const closeDocumentViewer = useCallback(() => {
    closeViewer();
    setViewerDocumentIds([]);
  }, [closeViewer]);
  const renderAiBadges = useRenderDocumentAiBadges({
    classificationStates,
    displayConfig: aiClassificationDisplayConfig,
    extractionEnabled,
    getExtraction: getExtractionForDocument,
    onOpen: openDocumentViewer,
  });

  const handleViewDocument: (document: DocumentViewerDocument) => void = openDocumentViewer;

  const renderBadges = useCallback(
    (doc: Parameters<typeof renderAiBadges>[0]) => {
      const reviewStatus = reviewByDocumentId.get(doc.id)?.status;
      const processingStatus =
        reviewStatus === 'processing' ? (
          <DocumentProcessingStatus
            onOpen={doc.signedUrl ? () => openDocumentViewer(doc) : undefined}
          />
        ) : null;

      if (displayContext === 'policyholder') {
        const conformity = visibleConformityByDocumentId.get(doc.id);
        return (
          <>
            {processingStatus}
            {conformity && <ConformityReadOnlyBadge conformity={conformity} />}
          </>
        );
      }

      if (processingStatus) return processingStatus;

      if (!hasReviewLayout) {
        return <span className="inline-flex items-center gap-1">{renderAiBadges(doc)}</span>;
      }

      if (reviewStatus !== 'attention') return null;

      return (
        <Button
          variant="ghost"
          size="sm"
          disabled={!doc.signedUrl}
          onClick={() => openDocumentViewer(doc)}
          className="text-amber-800 bg-amber-50"
        >
          {t('documentReview.status.attention')}
        </Button>
      );
    },
    [
      displayContext,
      hasReviewLayout,
      openDocumentViewer,
      renderAiBadges,
      reviewByDocumentId,
      t,
      visibleConformityByDocumentId,
    ],
  );

  const renderBelowRow = useCallback(
    (doc: { id: string }) => {
      const conformity = visibleConformityByDocumentId.get(doc.id);
      if (conformity?.decision !== 'REJECTED') return null;
      if (displayContext === 'policyholder') {
        return (
          <ConformityReuploadZone
            documentId={doc.id}
            reason={conformity.reason}
            onReupload={onReuploadDocumentConformity}
          />
        );
      }
      if (!conformity.reason) return null;
      return (
        <Text size="xs" tone="destructive" className="mx-3 mb-2.5 flex items-start gap-1.5">
          <AlertCircleIcon aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <Text inline weight="semibold">
              {t('documentsListsPolicy.conformity.rejectionReasonPrefix')}
            </Text>{' '}
            {conformity.reason}
          </span>
        </Text>
      );
    },
    [displayContext, onReuploadDocumentConformity, t, visibleConformityByDocumentId],
  );

  return (
    <>
      <Card>
        <CardHeader className="p-4 md:p-6">
          <Stack gap={2} className="md:flex-row md:justify-between md:items-start">
            <div>
              <CardTitle className="text-lg md:text-xl">{cardTitle}</CardTitle>
              {subtitle && (
                <div className="text-xs md:text-sm text-muted-foreground font-inter mt-2">
                  {subtitle}
                </div>
              )}
            </div>
            <Group gap={2}>
              {canDecideConformity && isDocumentReviewEnabled && !hideAcceptAllButton && (
                <ConformityAcceptAllControl
                  conformityByDocumentId={conformityByDocumentId}
                  summary={categoryConformitySummary}
                  onAcceptAll={onAcceptAllDocumentConformity}
                />
              )}
              {displayContext === 'broker' && extraActions}
            </Group>
          </Stack>
        </CardHeader>
        <Stack asChild>
          <CardContent className="p-4 md:p-6 md:pt-0">
            {hasReviewLayout && (
              <DocumentReviewFilters
                summary={reviewSummary}
                filter={reviewFilter}
                onFilterChange={setReviewFilter}
                showAttention={displayContext === 'broker'}
              />
            )}
            {documentsLists.length === 0 && <NoResultsRow />}
            {documentsLists.length > 0 && (
              <DocumentsCollection
                groups={getUploadedDocumentGroups({
                  documents: hasReviewLayout ? filteredDocuments : displayDocuments,
                  categories: hasReviewLayout ? filteredCategories : sortedDocumentsLists,
                  translate: t,
                }).sort((left, right) =>
                  hasReviewLayout
                    ? Number(right.documents.length > 0) - Number(left.documents.length > 0)
                    : 0,
                )}
                emptyCategories={emptyCategories}
                classificationCategories={readOnly ? undefined : documentsLists}
                upload={
                  readOnly
                    ? undefined
                    : {
                        onDrop: uploadQueue.addFiles,
                        multiple: allowMultiple,
                        minPages,
                        isUploading: uploadQueue.isUploading,
                        authorizedFormats,
                        helpText: t(
                          aiClassificationDisplayConfig?.enabled
                            ? 'fileInput.aiMaxFileSize'
                            : 'fileInput.uploadMaxFileSize',
                        ),
                        enforceAiSizeLimit: aiClassificationDisplayConfig?.enabled,
                      }
                }
                onView={handleViewDocument}
                onDelete={
                  canDelete
                    ? doc =>
                        deleteDocument({
                          policyId,
                          params: { ids: [doc.id], branchId },
                        })
                    : undefined
                }
                renderBadges={renderBadges}
                renderBelowRow={renderBelowRow}
                afterUpload={
                  <>
                    {hasReviewLayout &&
                      reviewFilter === 'attention' &&
                      reviewSummary.attentionCount === 0 && (
                        <Text size="sm" tone="muted">
                          {t('documentReview.noAttention')}
                        </Text>
                      )}
                    {hasReviewLayout &&
                      reviewFilter === 'missing' &&
                      missingRequiredCategories.length === 0 && (
                        <Text size="sm" tone="muted">
                          {t('documentReview.noMissing')}
                        </Text>
                      )}
                    <DocumentUploadQueue
                      attempts={uploadQueue.attempts}
                      receivedDocumentIds={new Set(viewableDocuments.map(({ id }) => id))}
                      onRetry={uploadQueue.retry}
                      onRemove={uploadQueue.remove}
                      isUploading={uploadQueue.isUploading}
                    />
                    {showUnclassifiedDocuments && unclassifiedDocs.length > 0 ? (
                      <TimedOutDocsSection
                        docs={unclassifiedDocs}
                        categories={sortedDocumentsLists.map(({ category, required }) => ({
                          category,
                          required,
                        }))}
                        onRetryAll={handleRetryTimedOutClassifications}
                        isRetrying={isClassifying}
                        onView={doc => {
                          if (doc.signedUrl) {
                            handleViewDocument({
                              id: doc.id,
                              name: doc.name,
                              signedUrl: doc.signedUrl,
                              createdAt: doc.createdAt,
                              type: doc.type,
                              category: doc.category,
                              metadata: doc.metadata as Record<
                                string,
                                string | number | boolean | undefined
                              >,
                            });
                          }
                        }}
                        onDelete={
                          canDelete
                            ? document =>
                                deleteDocument({
                                  policyId,
                                  params: { ids: [document.id], branchId },
                                })
                            : undefined
                        }
                        isBroker={displayContext === 'broker'}
                      />
                    ) : null}
                  </>
                }
              />
            )}
          </CardContent>
        </Stack>
      </Card>

      <DocumentViewer
        productId={policy.productId}
        review={selectedDocument ? reviewByDocumentId.get(selectedDocument.id) : undefined}
        onRetryAnalysis={
          displayContext === 'broker' &&
          !readOnly &&
          aiClassificationDisplayConfig?.enabled &&
          selectedDocument &&
          (selectedDocument.type === 'supporting_document' ||
            selectedDocument.type === 'non_delegated') &&
          selectedDocument.metadata.scanresult !== 'INFECTED'
            ? documentId => void processClassificationDocuments([documentId], { force: true })
            : undefined
        }
        isRetryingAnalysis={isClassifying}
        displayContext={displayContext}
        document={selectedDocument}
        documents={viewerDocuments}
        onClose={closeDocumentViewer}
        onDocumentChange={selectViewerDocument}
        onDelete={handleDeleteDocument}
        documentCustomCategorieMappings={readOnly ? undefined : documentsLists}
        uploadMode={uploadMode}
        extraction={selectedDocumentExtraction ?? null}
        extractionEnabled={extractionEnabled}
        onExtractionFeedback={extractionEnabled ? handleExtractionFeedback : undefined}
        isExtractionFeedbackPending={isFeedbackPending}
        validity={selectedDocumentValidity ?? null}
        validityEnabled={validityEnabled}
        onValidityOverride={
          displayContext === 'broker' && validityEnabled && canOverrideValidity
            ? handleValidityOverride
            : undefined
        }
        isValidityOverriding={isValidityOverriding}
        coherence={selectedDocument ? coherenceByDocumentId.get(selectedDocument.id) : undefined}
        fraud={canViewFraud ? selectedDocumentFraud : undefined}
        fraudPending={canViewFraud && selectedDocumentStream?.status === 'checking_fraud'}
        onCorrectFraudVerdict={canReviewFraud ? handleCorrectFraudVerdict : undefined}
        isCorrectingFraudVerdict={isCorrectingFraudVerdict}
        conformity={
          selectedDocument ? visibleConformityByDocumentId.get(selectedDocument.id) : undefined
        }
        onAcceptConformity={canDecideConformity ? onAcceptDocumentConformity : undefined}
        onRejectConformity={canDecideConformity ? onRejectDocumentConformity : undefined}
        onCancelConformity={canDecideConformity ? onCancelDocumentConformity : undefined}
      />
    </>
  );
}
